import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { documentsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Trash2, Loader2, FileText, ExternalLink, Pencil, Folder, Search, Eye, Download } from 'lucide-react';

const DOC_CATEGORIES = [
  'general', 'electrical', 'layout', 'joinery', 'plumbing', 'structural',
  'elevation', 'furniture', 'flooring', 'ceiling', 'hvac',
  'contract', 'proposal', 'blueprint', 'specification', 'warranty', 'permit', 'invoice', 'other'
];

const isPDF = (url) => url?.toLowerCase().endsWith('.pdf') || url?.includes('pdf');
const isImage = (url) => /\.(jpg|jpeg|png|gif|webp|svg|bmp)/i.test(url || '');
const DOC_STATUSES = ['draft', 'review', 'approved', 'signed', 'archived'];
const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-800', review: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-800', signed: 'bg-blue-100 text-blue-800', archived: 'bg-purple-100 text-purple-800',
};

export default function DocumentManagement({ projectId }) {
  const { isAdmin, isStaff } = useAuth();
  const canEdit = isAdmin || isStaff;
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewDoc, setViewDoc] = useState(null);
  const emptyForm = { title: '', category: 'general', file_url: '', file_name: '', description: '', version: '1.0', status: 'draft' };
  const [form, setForm] = useState(emptyForm);

  const fetchDocs = useCallback(async () => {
    try { const res = await documentsAPI.list(projectId); setDocs(res.data); }
    catch (err) { console.error(err); } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleSubmit = async (e) => {
    e.preventDefault(); setFormLoading(true);
    try {
      if (editing) { await documentsAPI.update(editing.document_id, form); }
      else { await documentsAPI.create(projectId, form); }
      setDialogOpen(false); setEditing(null); setForm(emptyForm); fetchDocs();
    } catch (err) { console.error(err); } finally { setFormLoading(false); }
  };

  const handleDelete = async (id) => {
    try { await documentsAPI.delete(id); fetchDocs(); } catch (err) { console.error(err); }
  };

  const openEdit = (d) => {
    setEditing(d);
    setForm({ title: d.title, category: d.category, file_url: d.file_url || '', file_name: d.file_name || '', description: d.description || '', version: d.version || '1.0', status: d.status });
    setDialogOpen(true);
  };

  if (loading) return <div className="animate-pulse h-48 bg-muted rounded" />;

  const grouped = {};
  docs.filter(d => {
    if (!searchTerm) return true;
    const t = searchTerm.toLowerCase();
    return d.title?.toLowerCase().includes(t) || d.category?.toLowerCase().includes(t) || d.description?.toLowerCase().includes(t);
  }).forEach(d => { const cat = d.category || 'general'; if (!grouped[cat]) grouped[cat] = []; grouped[cat].push(d); });

  return (
    <div className="space-y-6" data-testid="document-management">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h3 className="text-lg font-medium">Documents & Files</h3>
        <div className="flex items-center gap-2">
          <div className="relative flex-1 sm:w-52">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search files..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9 h-9" data-testid="doc-search-input" />
          </div>
          {canEdit && (<Button size="sm" onClick={() => { setForm(emptyForm); setEditing(null); setDialogOpen(true); }} data-testid="add-document-btn"><Plus className="h-4 w-4 mr-1" />Add Document</Button>)}
        </div>
      </div>

      {docs.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-muted-foreground"><FileText className="h-8 w-8 mx-auto mb-2 opacity-30" /><p className="text-sm">No documents yet</p></CardContent></Card>
      ) : (
        Object.entries(grouped).map(([cat, catDocs]) => (
          <div key={cat} className="space-y-2">
            <h4 className="text-sm font-medium capitalize text-muted-foreground flex items-center gap-2">
              <Folder className="h-4 w-4" />{cat.replace('_', ' ')} ({catDocs.length})
            </h4>
            <div className="space-y-2">
              {catDocs.map(doc => (
                <Card key={doc.document_id} data-testid={`doc-${doc.document_id}`}>
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium truncate">{doc.title}</p>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[doc.status] || STATUS_COLORS.draft}`}>{doc.status}</span>
                            <span className="text-[10px] text-muted-foreground">v{doc.version}</span>
                          </div>
                          {doc.description && <p className="text-xs text-muted-foreground truncate">{doc.description}</p>}
                          <p className="text-xs text-muted-foreground">By {doc.uploaded_by_name} — {new Date(doc.created_at).toLocaleDateString('en-IN', {day:'numeric',month:'short'})}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {doc.file_url && (
                          <>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setViewDoc(doc)} title="Preview" data-testid={`view-doc-${doc.document_id}`}>
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" asChild title="Open in new tab">
                              <a href={doc.file_url} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-3.5 w-3.5" /></a>
                            </Button>
                          </>
                        )}
                        {canEdit && (
                          <>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(doc)}><Pencil className="h-3.5 w-3.5" /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(doc.document_id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>{editing ? 'Edit Document' : 'Add Document'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2"><Label>Title</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} required data-testid="doc-title-input" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Category</Label>
                  <Select value={form.category} onValueChange={v => setForm({...form, category: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{DOC_CATEGORIES.map(c => <SelectItem key={c} value={c}><span className="capitalize">{c}</span></SelectItem>)}</SelectContent></Select>
                </div>
                <div className="space-y-2"><Label>Status</Label>
                  <Select value={form.status} onValueChange={v => setForm({...form, status: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{DOC_STATUSES.map(s => <SelectItem key={s} value={s}><span className="capitalize">{s}</span></SelectItem>)}</SelectContent></Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>File URL</Label><Input value={form.file_url} onChange={e => setForm({...form, file_url: e.target.value})} placeholder="https://..." /></div>
                <div className="space-y-2"><Label>Version</Label><Input value={form.version} onChange={e => setForm({...form, version: e.target.value})} /></div>
              </div>
              <div className="space-y-2"><Label>Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Optional" /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={formLoading} data-testid="save-doc-btn">{formLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}{editing ? 'Update' : 'Add Document'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* File Viewer Dialog */}
      <Dialog open={!!viewDoc} onOpenChange={() => setViewDoc(null)}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />{viewDoc?.title}
            </DialogTitle>
          </DialogHeader>
          {viewDoc?.file_url && (
            <div className="mt-2">
              {isPDF(viewDoc.file_url) ? (
                <div className="space-y-3">
                  <iframe
                    src={`${viewDoc.file_url}#toolbar=1&navpanes=0`}
                    className="w-full border rounded"
                    style={{ height: '60vh' }}
                    title={viewDoc.title}
                  />
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" asChild>
                      <a href={viewDoc.file_url} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-3.5 w-3.5 mr-2" />Open in Browser</a>
                    </Button>
                    <Button variant="outline" size="sm" asChild>
                      <a href={viewDoc.file_url} download><Download className="h-3.5 w-3.5 mr-2" />Download</a>
                    </Button>
                  </div>
                </div>
              ) : isImage(viewDoc.file_url) ? (
                <div className="space-y-3">
                  <img src={viewDoc.file_url} alt={viewDoc.title} className="w-full rounded border max-h-[60vh] object-contain" />
                  <Button variant="outline" size="sm" asChild>
                    <a href={viewDoc.file_url} download><Download className="h-3.5 w-3.5 mr-2" />Download</a>
                  </Button>
                </div>
              ) : (
                <div className="text-center py-8 space-y-3">
                  <FileText className="h-12 w-12 mx-auto text-muted-foreground/30" />
                  <p className="text-sm text-muted-foreground">Preview not available for this file type</p>
                  <div className="flex justify-center gap-2">
                    <Button variant="outline" size="sm" asChild>
                      <a href={viewDoc.file_url} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-3.5 w-3.5 mr-2" />Open in Browser</a>
                    </Button>
                    <Button variant="outline" size="sm" asChild>
                      <a href={viewDoc.file_url} download><Download className="h-3.5 w-3.5 mr-2" />Download</a>
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
